"use client";

import { motion } from "framer-motion";
import { fadeInUp, staggerContainer } from "@/lib/animations";
import { Coffee, UtensilsCrossed, Wifi, CreditCard } from "lucide-react";

const menuCategories = [
  {
    icon: Coffee,
    title: "Beverages",
    items: ["Specialty Coffee", "Fresh Juices", "Tea Selection", "Smoothies"],
    color: "text-amber-600 dark:text-amber-400",
    bgColor: "bg-amber-600/10",
    gradientFrom: "from-amber-500/20",
    gradientTo: "to-orange-500/5",
  },
  {
    icon: UtensilsCrossed,
    title: "Food",
    items: ["Fresh Pastries", "Healthy Snacks", "Sandwiches", "Salads"],
    color: "text-green-600 dark:text-green-400",
    bgColor: "bg-green-600/10",
    gradientFrom: "from-green-500/20",
    gradientTo: "to-emerald-500/5",
  },
  {
    icon: Wifi,
    title: "Services",
    items: [
      "Free WiFi",
      "Comfortable Seating",
      "Quiet Zones",
      "Outdoor Terrace",
    ],
    color: "text-blue-600 dark:text-blue-400",
    bgColor: "bg-blue-600/10",
    gradientFrom: "from-blue-500/20",
    gradientTo: "to-cyan-500/5",
  },
  {
    icon: CreditCard,
    title: "Pricing",
    items: [
      "Coffee from SAR 15",
      "Meals from SAR 30",
      "Member Discounts",
      "Daily Specials",
    ],
    color: "text-purple-600 dark:text-purple-400",
    bgColor: "bg-purple-600/10",
    gradientFrom: "from-purple-500/20",
    gradientTo: "to-pink-500/5",
  },
];

export default function CafeMenu() {
  return (
    <section
      id="cafe"
      className="py-16 sm:py-24 lg:py-32 px-4 sm:px-6 lg:px-8 bg-card/40 relative overflow-hidden"
    >
      {/* Background decorative elements */}
      <div className="absolute top-20 right-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-10 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />

      <div className="container mx-auto max-w-7xl relative z-10">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          {/* Section Header */}
          <motion.div
            variants={fadeInUp}
            className="text-center mb-12 sm:mb-16 lg:mb-20"
          >
            <h2 className="text-heading text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6 px-4">
              Cafe Menu
            </h2>
            <p className="text-text text-base sm:text-lg md:text-xl max-w-3xl mx-auto px-4 leading-relaxed">
              Fuel your creativity with our selection of premium coffee, healthy
              food, and comfortable amenities
            </p>
          </motion.div>

          {/* Menu Grid - Modern Layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {menuCategories.map((category, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="group relative"
              >
                {/* Card */}
                <div className="relative h-full bg-card/80 backdrop-blur-xl rounded-2xl lg:rounded-3xl border border-border shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden">
                  {/* Gradient Background */}
                  <div
                    className={`absolute inset-0 bg-gradient-to-br ${category.gradientFrom} ${category.gradientTo} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}
                  />

                  {/* Content Container */}
                  <div className="relative z-10 p-6 sm:p-7 lg:p-8 flex flex-col h-full">
                    {/* Icon */}
                    <div className="mb-6">
                      <div
                        className={`inline-flex w-16 h-16 lg:w-20 lg:h-20 rounded-2xl lg:rounded-3xl ${category.bgColor} items-center justify-center transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg`}
                      >
                        <category.icon
                          className={`w-8 h-8 lg:w-10 lg:h-10 ${category.color}`}
                        />
                      </div>
                    </div>

                    {/* Title */}
                    <h3 className="text-heading text-xl lg:text-2xl font-bold mb-6 group-hover:text-primary transition-colors duration-300">
                      {category.title}
                    </h3>

                    {/* Items List */}
                    <ul className="space-y-3 flex-grow">
                      {category.items.map((item, idx) => (
                        <motion.li
                          key={idx}
                          initial={{ opacity: 0, x: -10 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 + idx * 0.05 }}
                          className="flex items-start gap-3 text-text text-sm lg:text-base group/item"
                        >
                          <div className="relative mt-1.5 flex-shrink-0">
                            <div
                              className={`w-2 h-2 rounded-full bg-gradient-to-r ${category.gradientFrom
                                .replace("from-", "from-")
                                .replace("/20", "")} ${category.gradientTo
                                .replace("to-", "to-")
                                .replace(
                                  "/5",
                                  ""
                                )} group-hover/item:scale-150 transition-transform duration-300`}
                            />
                          </div>
                          <span className="leading-relaxed group-hover/item:translate-x-1 transition-transform duration-300">
                            {item}
                          </span>
                        </motion.li>
                      ))}
                    </ul>

                    {/* Decorative corner accent */}
                    <div
                      className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-br ${category.gradientFrom} ${category.gradientTo} opacity-0 group-hover:opacity-50 rounded-bl-full transition-opacity duration-500`}
                    />
                  </div>

                  {/* Bottom accent line */}
                  <div
                    className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${category.gradientFrom.replace(
                      "/20",
                      ""
                    )} ${category.gradientTo.replace(
                      "/5",
                      ""
                    )} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left`}
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
